import { useEffect, useState } from 'react';
import {
    Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, TextField, Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useNotify, useTranslate } from 'react-admin';
import {
    AccountDeletionRequestError,
    AccountDeletionRequestItem,
    approveAccountDeletionRequest,
    listAccountDeletionRequests,
    rejectAccountDeletionRequest,
} from '../services/accountDeletionRequests';

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

type Action = { type: 'approve' | 'reject'; item: AccountDeletionRequestItem } | null;

const RequestsList = ({ initialItems, showCompany }: { initialItems: AccountDeletionRequestItem[]; showCompany: boolean }) => {
    const notify = useNotify();
    const translate = useTranslate();
    const [items, setItems] = useState(initialItems);
    const [action, setAction] = useState<Action>(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const close = () => { setAction(null); setReason(''); };

    const reload = async () => {
        try {
            setItems(await listAccountDeletionRequests());
        } catch (e) {
            notify(e instanceof Error ? e.message : String(e), { type: 'error' });
        }
    };

    const run = async (task: () => Promise<void>, successKey: string, id: number) => {
        setBusy(true);
        try {
            await task();
            setItems(prev => prev.filter(item => item.id !== id));
            notify(translate(`admin.dashboard.deletionRequests.${successKey}`), { type: 'success' });
            close();
        } catch (e) {
            notify(e instanceof Error ? e.message : String(e), { type: 'error' });
            if (e instanceof AccountDeletionRequestError && (e.status === 404 || e.status === 409)) {
                close();
                await reload();
            }
        } finally {
            setBusy(false);
        }
    };

    if (items.length === 0) return null;

    const t = (key: string, options?: Record<string, string>) => translate(`admin.dashboard.deletionRequests.${key}`, options);
    const fullName = (item: AccountDeletionRequestItem) => `${item.firstName} ${item.lastName}`.trim();

    return (
        <Box sx={{ mb: 4 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PersonRemoveIcon color="warning" />
                        {t('title')}
                        <Chip label={items.length} size="small" color="warning" />
                    </Typography>
                    {items.map((item, idx) => (
                        <Box key={item.id}>
                            {idx > 0 && <Divider sx={{ my: 1 }} />}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={500}>
                                        {`${fullName(item)} (${item.email})`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        {t('requestedAt', { requestedAt: formatDate(item.requestedAt), dueAt: formatDate(item.dueAt) })}
                                    </Typography>
                                    {showCompany && (
                                        <Typography variant="caption" color="text.secondary" component="div">
                                            {item.companyName}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" color="success" variant="outlined" onClick={() => setAction({ type: 'approve', item })}>
                                        {t('approve')}
                                    </Button>
                                    <Button size="small" color="error" variant="outlined" onClick={() => setAction({ type: 'reject', item })}>
                                        {t('reject')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Dialog open={action?.type === 'approve'} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
                <DialogTitle>{t('approveTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {action ? t('approveText', { name: fullName(action.item) }) : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>{t('cancel')}</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={busy}
                        onClick={() => action && run(() => approveAccountDeletionRequest(action.item.id), 'approved', action.item.id)}
                    >
                        {t('approveConfirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={action?.type === 'reject'} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
                <DialogTitle>{t('rejectTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        required
                        multiline
                        minRows={3}
                        fullWidth
                        margin="dense"
                        label={t('rejectReason')}
                        helperText={t('rejectHelp')}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>{t('cancel')}</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={busy || reason.trim() === ''}
                        onClick={() => action && run(() => rejectAccountDeletionRequest(action.item.id, reason.trim()), 'rejected', action.item.id)}
                    >
                        {t('reject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const AccountDeletionRequestsCard = ({ showCompany = false }: { showCompany?: boolean }) => {
    const notify = useNotify();
    const [items, setItems] = useState<AccountDeletionRequestItem[] | null>(null);

    useEffect(() => {
        let active = true;
        listAccountDeletionRequests()
            .then(result => { if (active) setItems(result); })
            .catch(e => {
                if (!active) return;
                notify(e instanceof Error ? e.message : String(e), { type: 'error' });
                setItems([]);
            });
        return () => { active = false; };
    }, [notify]);

    if (!items || items.length === 0) return null;

    return <RequestsList initialItems={items} showCompany={showCompany} />;
};

export default AccountDeletionRequestsCard;
